import {
  getDeviceType,
  getPrimaryBackhaulApRadio,
  normalizeBand,
} from './channelMapUtils';

function isCapDevice(device) {
  return getDeviceType(device?.capabilities) === 'CAP';
}

function formatChannelBandLabel(channel, band) {
  const ch = Number(channel);
  const normalizedBand = band ? normalizeBand(band) : null;
  if (Number.isFinite(ch) && ch > 0 && normalizedBand && normalizedBand !== 'unknown') {
    return `ch ${ch} · ${normalizedBand}`;
  }
  if (Number.isFinite(ch) && ch > 0) {
    return `ch ${ch}`;
  }
  if (normalizedBand && normalizedBand !== 'unknown') {
    return normalizedBand;
  }
  return null;
}

function isPlaceholderUplinkChannel(channel, band) {
  const bandStr = String(band || '').toLowerCase();
  if (bandStr.includes('auto')) return true;
  const ch = Number(channel);
  return ch === -1;
}

function resolveStaRadioInfo(device) {
  const privateInterfaces = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces;
  if (!Array.isArray(privateInterfaces)) return null;

  const staIface = privateInterfaces.find((iface) => {
    const mode = String(iface?.Mode || iface?.mode || '').toLowerCase();
    return mode === 'station' || mode === 'sta';
  });
  if (!staIface) return null;

  const radios = device?.radios || [];
  const radioMap = {};
  radios.forEach((r) => {
    if (r?.name) radioMap[String(r.name).toLowerCase()] = r;
  });

  const radioName = String(
    staIface?.Radio?.Name || staIface?.radio?.name || staIface?.radio?.Ref || '',
  ).toLowerCase();
  if (radioName && radioMap[radioName]) return radioMap[radioName];

  const ifaceListIdx = privateInterfaces.indexOf(staIface);
  if (radios.length > 0) {
    const ifaceIdx =
      parseInt(String(staIface?.Name || staIface?.name || '').replace(/\D/g, ''), 10) || ifaceListIdx + 1;
    const byIndex = radios.find((r) => {
      const rIdx = parseInt(String(r?.name || '').replace(/\D/g, ''), 10);
      return rIdx === ifaceIdx;
    });
    if (byIndex) return byIndex;
    return radios[Math.min(ifaceListIdx, radios.length - 1)];
  }
  return null;
}

function getStaUplinkChannelBandLabel(device) {
  if (!device) return null;

  const rawCh = device.ul_channel;
  const numCh = typeof rawCh === 'number' ? rawCh : parseInt(rawCh, 10);
  const band = device.ul_band;

  if (Number.isFinite(numCh) && numCh > 0 && !isPlaceholderUplinkChannel(numCh, band)) {
    const label = formatChannelBandLabel(numCh, band);
    if (label) return label;
  }

  const radioInfo = resolveStaRadioInfo(device);
  if (radioInfo) {
    const ch = Number(radioInfo.channel);
    const radioBand = radioInfo.band || band;
    if (Number.isFinite(ch) && ch > 0) {
      return formatChannelBandLabel(ch, radioBand);
    }
  }

  return null;
}

function getApBackhaulChannelBandLabel(device) {
  const ap = getPrimaryBackhaulApRadio(device);
  if (!ap) return null;
  return formatChannelBandLabel(ap.channel, ap.band);
}

/**
 * Resolve the channel/band label shown beside an iperf test row.
 * Uplink tests use the child STA channel (from_serial).
 * Downlink / CAP→device tests use the CAP (or parent) backhaul AP channel
 * when from_serial is a CAP with a placeholder ul_channel (e.g. ch 1 · auto).
 */
export function resolveIperfTestChannelBand(test, deviceLookup, { reverseDirection = false } = {}) {
  if (!test || !deviceLookup) return null;

  const fromDev = test.from_serial
    ? deviceLookup.get(String(test.from_serial).toLowerCase())
    : null;
  const toDev = test.to_serial
    ? deviceLookup.get(String(test.to_serial).toLowerCase())
    : null;

  if (reverseDirection) {
    if (fromDev && isCapDevice(fromDev)) {
      return getApBackhaulChannelBandLabel(fromDev)
        || getStaUplinkChannelBandLabel(toDev);
    }
    return getStaUplinkChannelBandLabel(toDev)
      || getApBackhaulChannelBandLabel(fromDev);
  }

  if (fromDev && isCapDevice(fromDev)) {
    return getApBackhaulChannelBandLabel(fromDev)
      || getStaUplinkChannelBandLabel(fromDev);
  }

  return getStaUplinkChannelBandLabel(fromDev);
}
